import { removeNull } from '@/utils/handleData.js'
import { buildFileList, splitPicUids } from '@/utils/signedDoc.js'

function pad2(n) {
  return n < 10 ? '0' + n : '' + n
}

function formatDateTime(value) {
  if (value == null || value === '') return ''
  if (Array.isArray(value) && value.length >= 3) {
    const y = value[0]
    const m = pad2(value[1])
    const d = pad2(value[2])
    const hh = pad2(value[3] || 0)
    const mm = pad2(value[4] || 0)
    const ss = pad2(value[5] || 0)
    return y + '-' + m + '-' + d + ' ' + hh + ':' + mm + ':' + ss
  }
  if (typeof value === 'string') {
    const text = value.trim()
    if (!text) return ''
    if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(text)) {
      return text.replace('T', ' ').slice(0, 19)
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
      return text + ' 00:00:00'
    }
    const d = new Date(text.replace(/-/g, '/'))
    if (!isNaN(d.getTime())) {
      return (
        d.getFullYear() +
        '-' +
        pad2(d.getMonth() + 1) +
        '-' +
        pad2(d.getDate()) +
        ' ' +
        pad2(d.getHours()) +
        ':' +
        pad2(d.getMinutes()) +
        ':' +
        pad2(d.getSeconds())
      )
    }
    return text
  }
  if (typeof value === 'number') {
    const d = new Date(value)
    if (!isNaN(d.getTime())) {
      return formatDateTime(d)
    }
  }
  if (value instanceof Date && !isNaN(value.getTime())) {
    return (
      value.getFullYear() +
      '-' +
      pad2(value.getMonth() + 1) +
      '-' +
      pad2(value.getDate()) +
      ' ' +
      pad2(value.getHours()) +
      ':' +
      pad2(value.getMinutes()) +
      ':' +
      pad2(value.getSeconds())
    )
  }
  return String(value)
}

function formatNoticeStatus(status) {
  const text = String(status == null ? '' : status).trim()
  if (text === '0' || text === '草稿') return '草稿'
  if (text === '1' || text === '已发布') return '已发布'
  return text
}

function toStatusCode(status, fallback) {
  const text = String(status == null ? '' : status).trim()
  if (text === '0' || text === '草稿') return '0'
  if (text === '1' || text === '已发布') return '1'
  if (text) return text
  return fallback == null ? '1' : fallback
}

function toUidArray(value) {
  if (value == null || value === '') return []
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item && typeof item === 'object') {
          return String(item.uid || item.value || item.orgUid || '').trim()
        }
        return String(item || '').trim()
      })
      .filter(Boolean)
  }
  return splitPicUids(value)
}

function formatScopeName(scopename, scopeUids) {
  const text = String(scopename || '').trim()
  if (text) {
    if (/\d+、/.test(text)) return text
    const parts = text
      .split(/[、,，;；]/)
      .map((item) => item.trim())
      .filter(Boolean)
    if (!parts.length) return text
    return parts
      .map((name, index) => index + 1 + '、' + name + '；' + '\xa0')
      .join('')
  }
  if (scopeUids && scopeUids.length) {
    return scopeUids.join('、')
  }
  return ''
}

function buildNoticePayload(data, sourceOrgUid) {
  const source = data || {}
  const payload = {
    messageTitle: source.messageTitle || '',
    messageType: source.messageType || '',
    messageScope: toUidArray(source.messageScope),
    messageContent: source.messageContent || '',
    messageEnclosure: toUidArray(source.messageEnclosure),
    messageStatus: toStatusCode(source.messageStatus, '1'),
    sourceOrgUid:
      source.sourceOrgUid || source.orgUid || sourceOrgUid || ''
  }
  if (source.uid) payload.uid = source.uid
  return payload
}

function normalizeNoticeList(list) {
  const data = list && typeof list === 'object' ? list : { result: [], totalSize: 0 }
  const result = Array.isArray(data.result) ? data.result : []
  data.result = result.map((item) => {
    const row = Object.assign({}, item)
    row.messageStatus = formatNoticeStatus(
      row.messageStatus || row.message_status
    )
    row.updateTime = formatDateTime(row.updateTime || row.update_time)
    row.personName = row.personName || row.person_name || ''
    row.scopename = row.scopename || row.scopeName || row.messageScopeName || ''
    return row
  })
  return data
}

function buildApprovalStatusTip(item) {
  if (!isDoneApproval(item.taskStatus)) return ''
  const parts = []
  const auditTime =
    item.auditTime || item.audit_time || item.updateTime || item.update_time
  if (auditTime) {
    parts.push('审核时间：' + formatDateTime(auditTime))
  }
  const mark = String(
    item.mark || item.approvleMark || item.approvle_mark || item.review || ''
  ).trim()
  if (item.taskStatus === '审核拒绝' && mark) {
    parts.push('审核意见：' + mark)
  }
  return parts.join('；')
}

function isRectifyApproval(item) {
  const table = String((item && (item.tableName || item.bizTable || item.table_name)) || '')
  const text = String(
    (item &&
      (item.titleType || item.content || item.title || item.takasName || '')) ||
      ''
  )
  if (table.indexOf('edu_class') > -1) return true
  return /整改/.test(text) && !/审核|审批/.test(text)
}

function mapApprovalTaskStatus(item) {
  const raw = String((item && item.taskStatus) || '').trim()
  const mark = String(
    (item && (item.mark || item.approvleMark || item.approvleResult || item.review)) ||
      ''
  ).trim()
  const result = String(
    (item && (item.approvleResult || item.approveResult || item.auditStatus)) || ''
  ).trim()
  const rectify = isRectifyApproval(item)
  if (rectify) {
    if (
      raw === '已整改' ||
      mark === '已整改' ||
      raw === '已审批' ||
      raw === '1' ||
      raw === '审核通过'
    ) {
      return '已整改'
    }
    return '待整改'
  }
  if (
    raw === '审核拒绝' ||
    raw === '已拒绝' ||
    raw === '0' ||
    result === '0' ||
    /拒绝/.test(raw) ||
    /拒绝/.test(mark)
  ) {
    return '审核拒绝'
  }
  if (
    raw === '审核通过' ||
    raw === '已审批' ||
    raw === '已通过' ||
    raw === '1' ||
    result === '1'
  ) {
    return '审核通过'
  }
  if (raw === '待审批' || raw === '待审核' || raw === '2' || !raw) {
    return '待审核'
  }
  return raw
}

function isDoneApproval(status) {
  return (
    status === '审核通过' ||
    status === '审核拒绝' ||
    status === '已整改' ||
    status === '已审批'
  )
}

/** 半个月（15 天）起点，供待办列表 beginTime 过滤 */
export function taskHalfMonthAgo() {
  const d = new Date()
  d.setDate(d.getDate() - 15)
  const pad = (n) => (n < 10 ? '0' + n : '' + n)
  return (
    d.getFullYear() +
    '-' +
    pad(d.getMonth() + 1) +
    '-' +
    pad(d.getDate()) +
    ' 00:00:00'
  )
}

export default {
  namespaced: true,
  state: {
    taskNoticeNum: 0, // 缓存当前用户未办理代办数量
    tabCounts: {
      approval: 0,
      warning: 0,
      message: 0
    },
    approvalList: {}, // 缓存当前用户审批整改数据
    warningList: {}, // 缓存当前用户预警提醒信息
    messageList: {}, // 缓存当前用户通知公告接收列表
    noticeList: {}, // 缓存当前机构通知公告列表
    noticeInfo: {} // 缓存通知公告详情
  },
  actions: {
    /********************************************** 待办任务 ********************************************************/

    // 获取审批整改列表（仅半个月内）
    async getApprovalList({ rootState, commit, dispatch }) {
      var queryValues = rootState.queryValues.approval
        ? { ...rootState.queryValues.approval }
        : {}
      queryValues.page = rootState.page.approval
      queryValues.limit = rootState.limit.approval
      if (!queryValues.beginTime) {
        queryValues.beginTime = taskHalfMonthAgo()
      }
      queryValues = removeNull(queryValues)
      const res = await dispatch(
        'baseRequest',
        { method: 'GET', url: '/query/flow/list', data: queryValues },
        { root: true }
      )
      if (res.data && Array.isArray(res.data.result)) {
        res.data.result.forEach((item) => {
          item.taskStatus = mapApprovalTaskStatus(item)
          item.tableName = item.tableName || item.table_name || ''
          item.pageCode = item.pageCode || item.page_code || ''
          item.mark =
            item.mark ||
            item.approvleMark ||
            item.approvle_mark ||
            item.review ||
            ''
          item.auditTime = formatDateTime(
            item.auditTime ||
              item.audit_time ||
              item.updateTime ||
              item.update_time ||
              ''
          )
          item.statusTip = buildApprovalStatusTip(item)
          item.disabledButtons = []
          if (isDoneApproval(item.taskStatus)) {
            item.disabledButtons = [0]
          }
        })
        commit(
          'baseSet',
          { module: 'taskNotice', key: 'approvalList', value: res.data },
          { root: true }
        )
      }
      await dispatch('refreshUnreadCount')
    },

    async getWarningUnread({ dispatch }) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/todo/alert/unread',
          data: {},
          noErrorMsg: true
        },
        { root: true }
      )
      return res && res.data != null ? Number(res.data) || 0 : 0
    },

    async getMessageUnread({ rootState, dispatch }) {
      try {
        const res = await dispatch(
          'baseRequest',
          {
            method: 'GET',
            url: '/act/message/info/inbox',
            data: {
              page: 1,
              limit: 1,
              orgUid: rootState.user.orgValue
            },
            noErrorMsg: true
          },
          { root: true }
        )
        const data = res && res.data
        if (!data) return 0
        return Number(data.totalSize || data.total || 0) || 0
      } catch (e) {
        return 0
      }
    },

    /** 登录态未读总数：流程待办 + 预警，供角标实时刷新 */
    async refreshUnreadCount({ rootState, commit, dispatch }) {
      let flowUnread = 0
      try {
        const res = await dispatch(
          'baseRequest',
          {
            method: 'GET',
            url: '/act/flow/unreadcount',
            data: {},
            noErrorMsg: true
          },
          { root: true }
        )
        const data = res && res.data
        if (typeof data === 'number') {
          flowUnread = data
        } else if (data && typeof data.count === 'number') {
          flowUnread = data.count
        } else {
          const n = Number(data)
          flowUnread = isNaN(n) ? 0 : n
        }
      } catch (e) {
        flowUnread = 0
      }
      const warnUnread = await dispatch('getWarningUnread')
      const messageUnread = await dispatch('getMessageUnread')
      const total = (flowUnread || 0) + (warnUnread || 0)
      const prev = Number(rootState.taskNotice.taskNoticeNum) || 0
      commit(
        'baseSet',
        {
          module: 'taskNotice',
          key: 'taskNoticeNum',
          value: total
        },
        { root: true }
      )
      commit(
        'baseSet',
        {
          module: 'taskNotice',
          key: 'tabCounts',
          value: {
            approval: flowUnread || 0,
            warning: warnUnread || 0,
            message: messageUnread || 0
          }
        },
        { root: true }
      )
      return { total, prev, increased: total > prev }
    },

    // 预警消息列表
    async getWarningList({ rootState, commit, dispatch }) {
      var queryValues = rootState.queryValues.warning
        ? { ...rootState.queryValues.warning }
        : {}
      queryValues.page = rootState.page.warning || 1
      queryValues.limit = rootState.limit.warning || 10
      queryValues = removeNull(queryValues)
      const res = await dispatch(
        'baseRequest',
        { method: 'GET', url: '/organize/todo/alert/list', data: queryValues },
        { root: true }
      )
      const data = res.data || { result: [], totalSize: 0 }
      if (data.result) {
        data.result.forEach((item) => {
          item.titleType = item.titleType || item.title_type || item.title || ''
          item.content = item.content || item.takasContent || item.takas_content || ''
          item.orgName = item.orgName || item.org_name || ''
          item.launchTime = formatDateTime(
            item.launchTime || item.launch_time || item.createTime || item.create_time
          )
          item.taskStatus =
            item.taskStatus ||
            item.task_status ||
            (item.status === '0'
              ? '待处理'
              : item.status === '2'
              ? '已处理'
              : '已读')
          item.disabledButtons = []
        })
      }
      commit(
        'baseSet',
        { module: 'taskNotice', key: 'warningList', value: data },
        { root: true }
      )
      await dispatch('refreshUnreadCount')
      return data
    },

    // 通知公告接收列表（当前机构范围）
    async getInboxNoticeList({ rootState, commit, dispatch }) {
      var queryValues = rootState.queryValues.message
        ? { ...rootState.queryValues.message }
        : {}
      queryValues.page = rootState.page.message || 1
      queryValues.limit = rootState.limit.message || 10
      queryValues.orgUid = rootState.user.orgValue
      queryValues = removeNull(queryValues)
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/act/message/info/inbox',
          data: queryValues
        },
        { root: true }
      )
      const data = res.data || { result: [], totalSize: 0 }
      if (data.result) {
        data.result.forEach((item) => {
          item.titleType =
            item.titleType || item.title_type || item.messageTitle || ''
          item.takasSource =
            item.takasSource || item.takas_source || item.messageType || ''
          item.content =
            item.content || item.takas_content || item.messageContent || ''
          item.orgName = item.orgName || item.org_name || ''
          item.launchTime = formatDateTime(
            item.launchTime || item.launch_time || item.notice_time || item.create_time
          )
          item.pageCode = item.pageCode || item.page_code || 'notice'
        })
      }
      commit(
        'baseSet',
        { module: 'taskNotice', key: 'messageList', value: data },
        { root: true }
      )
      return data
    },

    async markWarningRead({ dispatch }, uid) {
      await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/todo/alert/read',
          data: { uid: uid }
        },
        { root: true }
      )
      await dispatch('refreshUnreadCount')
    },

    // 修改审核通知信息阅读状态
    async editNoticeStatus({ dispatch }, uid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/act/flow/read',
          data: { uid: uid }
        },
        { root: true }
      )
      await dispatch('refreshUnreadCount')
      return res
    },

    /********************************************** 通知公告 ********************************************************/

    // 发布通知公告
    async addNotice({ dispatch, rootState }, data) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'POST',
          url: '/act/message/info',
          data: buildNoticePayload(data, rootState.user && rootState.user.orgValue),
          msg: '新增通知公告成功'
        },
        { root: true }
      )
      return res
    },

    // 删除通知公告
    async deletNotice({ dispatch }, uid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'DELETE',
          url: '/act/message/info',
          data: { uid: uid },
          msg: '删除通知公告成功'
        },
        { root: true }
      )
      return res
    },

    // 编辑通知公告
    async editNotice({ dispatch, rootState }, data) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/act/message/info',
          data: buildNoticePayload(data, rootState.user && rootState.user.orgValue),
          msg: '编辑通知公告成功'
        },
        { root: true }
      )
      return res
    },

    // 获取通知公告详情
    async getNoticeInfo({ commit, dispatch, rootState }, uid) {
      const id =
        uid && typeof uid === 'object' ? uid.uid || uid.messageUid : uid
      if (!id) {
        commit(
          'baseSet',
          { module: 'taskNotice', key: 'noticeInfo', value: {} },
          { root: true }
        )
        return null
      }
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/act/message/info/detail',
          data: { uid: id, messageUid: id }
        },
        { root: true }
      )
      let raw = res && res.data
      if (Array.isArray(raw)) raw = raw[0]
      if (raw) {
        const enclosure = toUidArray(
          raw.messageEnclosure || raw.message_enclosure || ''
        )
        commit(
          'pictureSet',
          { key: 'messageEnclosure', value: enclosure },
          { root: true }
        )
        const scopeUids = toUidArray(
          raw.messageScope || raw.message_scope || raw.orgUid || raw.org_uid
        )
        const scopeName = formatScopeName(
          raw.messageScopeName || raw.scopename || raw.scopeName,
          scopeUids
        )
        const info = {
          uid: raw.uid || id,
          messageTitle: raw.messageTitle || raw.message_title || '',
          messageType: raw.messageType || raw.message_type || '',
          messageStatus: toStatusCode(
            raw.messageStatus || raw.message_status,
            '1'
          ),
          messageContent: raw.messageContent || raw.message_content || '',
          messageScope: scopeUids,
          messageScopeName: scopeName,
          scopename: raw.scopename || scopeName,
          messageEnclosure: enclosure,
          sourceOrgUid: raw.sourceOrgUid || raw.source_org_uid || '',
          updateTime: formatDateTime(raw.updateTime || raw.update_time || ''),
          personName: raw.personName || raw.person_name || '',
          fileLists: {
            messageEnclosure:
              (rootState.pictures && rootState.pictures.messageEnclosure) ||
              buildFileList({ state: rootState }, enclosure, '附件')
          }
        }
        commit(
          'baseSet',
          { module: 'taskNotice', key: 'noticeInfo', value: info },
          { root: true }
        )
        return info
      }
      commit(
        'baseSet',
        { module: 'taskNotice', key: 'noticeInfo', value: {} },
        { root: true }
      )
      return null
    },

    // 获取通知公告列表
    async getNoticeList({ rootState, commit, dispatch }) {
      var queryValues = rootState.queryValues.notice
        ? { ...rootState.queryValues.notice }
        : {}
      queryValues.page = rootState.page.notice
      queryValues.limit = rootState.limit.notice
      queryValues = removeNull(queryValues)
      const res = await dispatch(
        'baseRequest',
        { method: 'GET', url: '/act/message/list', data: queryValues },
        { root: true }
      )
      const noticeList = normalizeNoticeList(
        res && res.data ? res.data : { result: [], totalSize: 0 }
      )
      commit(
        'baseSet',
        { module: 'taskNotice', key: 'noticeList', value: noticeList },
        { root: true }
      )
    }
  }
}
